import type { DestroyOptions, Point } from 'pixi.js';
import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { registerResizableObject } from '../../utils/pixi/registerResizableObject';
import { TopBarLayout } from '../../../layouts/TopBarLayout';
import { processDynamicPropsObject } from './utils/processDynamicPropsObject';
import type { AppStateController } from '../../app/state/AppStateController';
import { GAME_OBJECT_POOL } from '../GameConstants';
import { setContainerParent } from '../../utils/pixi/transform-utils';
import { highlightContainer } from '../../utils/pixi/animations/highlightContainer';
import type { LevelTaskProgress, LevelTaskType } from '../tasks/types';
import type { LevelTasksViewGroup } from '../tasks/views/types';
import { createTasksViewGroup } from '../tasks/views/createTasksViewGroup';
import { CoinCounterView } from './common/counters/CoinCounterView';
import { LivesCounterView } from './common/counters/LivesCounterView';

export class TopBarView extends Container {
  public readonly layout = new TopBarLayout();

  private readonly coinCounter: CoinCounterView;

  private readonly livesCounter: LivesCounterView;

  private tasksGroup!: LevelTasksViewGroup;

  public readonly onSettingsButtonClicked = new Signal<() => void>();

  public readonly onMoneyCounterClicked = new Signal();

  constructor(
    private readonly state: AppStateController,
    tasks: LevelTaskProgress[],
    iapsAvailable: boolean,
  ) {
    super();

    this.livesCounter = new LivesCounterView(state).props({
      parent: this.layout.livesContainerSlot,
      alpha: 0,
    });

    this.coinCounter = new CoinCounterView(state, iapsAvailable).props({
      parent: this.layout.coinContainerSlot,
    });
    this.coinCounter.onClicked.connect(() => {
      this.onMoneyCounterClicked.emit();
    });

    this.state.subscriptions.onChange(
      ['coins'],
      () => {
        this.updateCoins(true);
      },
      this,
    );
    this.on('destroyed', () => {
      state.subscriptions.removeAllSubscriptions(this);
    });

    this.tasksGroup = createTasksViewGroup(tasks, { forceBasic: true, incompleteOnly: false });
    this.layout.tasksContainer.addChild(this.tasksGroup.container);

    this.init();
    this.zIndex = 150;

    this.updateCoins(false);

    registerResizableObject(this);
  }

  public resetTasksGroup(tasks: LevelTaskProgress[]): void {
    this.tasksGroup?.container?.destroy({ children: true });
    this.tasksGroup = createTasksViewGroup(tasks, { forceBasic: true, incompleteOnly: false });
    this.layout.tasksContainer.addChild(this.tasksGroup.container);
  }

  public destroy(options?: DestroyOptions): void {
    this.livesCounter.destroy(options);
    this.coinCounter.destroy(options); // may have been re-parented.
    super.destroy(options);
  }

  private init(): void {
    const { root, settingsButton } = this.layout;

    this.addChild(root);

    settingsButton.on('pointertap', (): void => {
      this.onSettingsButtonClicked.emit();
    });

    registerResizableObject(this);
  }

  public incrementTaskProgress(taskType: LevelTaskType, amount: number): LevelTaskProgress | undefined {
    const taskView = this.tasksGroup.views.get(taskType);
    if (!taskView) return undefined;
    return taskView.incrementProgress(amount);
  }

  public allTasksVisuallyComplete(): boolean {
    return this.tasksGroup.allTasksVisuallyComplete();
  }

  public updateCoins(animate: boolean): void {
    this.coinCounter.updateAmount(this.state.getCoins(), animate);
  }

  public getCoinCounter(): CoinCounterView {
    return this.coinCounter;
  }

  public setCoinCounterParent(parent: Container): void {
    const coinView = this.getCoinCounter();
    if (coinView.parent === parent) return;
    setContainerParent(coinView, parent);
  }

  public getTasksContainer(): Container {
    return this.layout.tasksContainer;
  }

  public resetCoinContainerParent(): void {
    this.setCoinCounterParent(this.layout.coinContainerSlot);
  }

  /**
   * @param newParent progressContainer will be re-parented to this container until reset.
   */
  public highlightGoal(newParent: Container): { resetHighlight: () => void; target: Container } {
    const { tasksContainer } = this.layout;
    return { resetHighlight: highlightContainer(tasksContainer, newParent), target: tasksContainer };
  }

  public getGoalIconGlobalPos(taskType: LevelTaskType): Point {
    const taskView = this.tasksGroup.views.get(taskType);
    if (!taskView) return GAME_OBJECT_POOL.getPoint().set(0, 0);

    return taskView.getScoreBubblesTarget();
  }

  public onGoalHit(taskType: LevelTaskType): void {
    const taskView = this.tasksGroup.views.get(taskType);
    taskView?.onGoalHit();
  }

  public setFailFlowState(value: boolean): void {
    const { settingsButton, tasksContainer, coinContainerSlot } = this.layout;

    [settingsButton, tasksContainer].forEach((container) => {
      container.tweenTo({ alpha: value ? 0 : 1 }, 300, 'sinIn', true);
    });

    // Keep the coin balance visible above the popup (the coin-revive option needs it).
    setContainerParent(this.coinCounter, value ? this.pixi.stage : coinContainerSlot);
  }

  public resize(width: number, height: number): void {
    const { settingsButton, tasksContainer } = this.layout;
    processDynamicPropsObject(settingsButton, width, height);

    // Make sure that the tasks container never overlaps with the coin counter
    const tasksContainerMargin = 6;

    const coinCounterOccupiedWidth = this.coinCounter.x + this.coinCounter.width;
    const availableWidth = Math.max(width - coinCounterOccupiedWidth - settingsButton.width - tasksContainerMargin, 0);

    // If there's enough space, center the tasksContainer. Otherwise center it in the space between the coin counter and the settings button
    tasksContainer.x =
      width * 0.5 - tasksContainer.width * 0.5 >= coinCounterOccupiedWidth ? width * 0.5 : coinCounterOccupiedWidth + availableWidth * 0.5;
  }
}
